/**
 * Versioned migration for persisted progress data.
 *
 * The hydration path calls `migrateProgressData(raw)` exactly once on boot.
 * Whenever a new field is added to the progress slice OR the shape changes
 * incompatibly, add it to `FALLBACK` and bump CURRENT_SCHEMA_VERSION, then
 * append a migration step below.
 *
 * Guarantees:
 *   - Pure: same input → same output.
 *   - Idempotent: applying twice produces the same result.
 *   - Additive: never removes user data; only fills in missing fields.
 *   - Safe on corrupt/null input: returns a fresh slice without throwing.
 */
import { DEFAULT_AVATAR } from '../data/avatarCatalog.jsx';

export const CURRENT_SCHEMA_VERSION = 8;

/** Cap on the number of entries retained in `exerciseHistory`. The list is
 *  used by the "Επανάληψη όλων" mode to sample recent attempts; older
 *  entries fall off the back. */
export const EXERCISE_HISTORY_LIMIT = 200;

/** Cap on the number of days retained in `dailyQuestHistory`. The archive
 *  drives the calendar make-up flow; only the last few months are useful
 *  for that. Older entries are pruned on rollover. */
export const DAILY_QUEST_HISTORY_DAYS = 90;

/**
 * Canonical default shape of a fresh progress slice. Used as the floor for
 * every known field during migration. Never mutate at runtime — copies are
 * returned by `freshProgress()`.
 *
 * Default exercise-type stats keys MUST match the IDs returned by
 * `pickExerciseTypesForSession` and consumed by `commitSession`.
 */
export const DEFAULT_EXERCISE_TYPE_STATS = Object.freeze({
  fillTheBlank: 0,
  dictation: 0,
  multipleChoice: 0,
  findTheMistake: 0,
});

/**
 * v8 sync-layer metadata defaults. DORMANT — nothing in the offline app reads
 * these; they exist so the (later, flag-gated) sync layer can attach without
 * another schema bump. Mirrors `src/sync/schemaV8.js` DEFAULT_SYNC_META.
 */
export const DEFAULT_SYNC_META = Object.freeze({
  baselineUploaded: false,
  lastSyncedVersion: 0,
  lastPulledSeq: 0,
});

const FALLBACK = Object.freeze({
  schemaVersion: CURRENT_SCHEMA_VERSION,
  xp: 0,
  currentStreak: 0,
  bestStreak: 0,
  lastSessionDate: null,
  badges: [],
  totalSessions: 0,
  perfectSessions: 0,
  weaknessProfile: {},
  itemTracking: {},
  dailyQuests: { date: null, quests: [] },
  pet: { growth: 0, stage: 0 },
  avatar: { ...DEFAULT_AVATAR },
  completedTrainingDays: [],
  // ── v3 tracking ────────────────────────────────────────────────────────
  // Best consecutive run of "correct on first attempt" answers, across
  // sessions. Resets on any wrong-on-first-attempt answer. Powers the
  // "X correct in a row" badge family.
  bestCorrectStreak: 0,
  currentCorrectStreak: 0,
  // Number of review/assessment phases the child has fully completed.
  // Increments only when a session with a review phase commits.
  reviewsCompleted: 0,
  // Cumulative count of daily quests ever completed (any type).
  dailyQuestsCompleted: 0,
  // Per-exercise-type correct-answer counter. Drives the exercise-type
  // diversity badges. Items are categorised by the type assigned by
  // `pickExerciseTypesForSession`, not by the item's intrinsic shape.
  exerciseTypeStats: { ...DEFAULT_EXERCISE_TYPE_STATS },
  // Last grade the child played (string '2'|'3'|'4'), used to route a
  // tapped daily quest into the appropriate session without forcing a
  // grade re-selection. null on first launch.
  lastPlayedGrade: null,
  // ── v4 tracking ────────────────────────────────────────────────────────
  // Rolling log of recent exercise attempts (any source: regular session,
  // mistakes recap, grammar rule game, dictionary game, history review).
  // Capped at EXERCISE_HISTORY_LIMIT entries; oldest are evicted first.
  // Each entry: { itemId, kind, attemptedAt: ISO, isCorrect, grade }.
  // Drives the "Επανάληψη όλων" mode and the weekly mandatory review.
  exerciseHistory: [],
  // ISO dates ('YYYY-MM-DD', Mon-Fri only) on which the weekly mandatory
  // review quest has been completed. Used by the quest picker to know
  // whether THIS Mon-Fri week still needs the review or not.
  weeklyReviewDates: [],
  // ── v5 tracking ────────────────────────────────────────────────────────
  // Archive of daily quest slices keyed by their ISO date. Populated on
  // day-rollover by `rolloverDailyQuestsIfNeeded` (the new logic pushes
  // the previous day's slice in here before generating today's). Drives
  // the calendar make-up flow: tapping a missed past day reads the
  // quests-as-they-were and lets the child still finish them.
  // Capped at DAILY_QUEST_HISTORY_DAYS most recent entries.
  dailyQuestHistory: {},
  // Earliest date on which the child made any committed progress. Set
  // once on the first commit after migration; never moves backwards.
  // Used to gate the calendar make-up flow so a brand-new account can't
  // see "Δευτέρα missed" before they ever played.
  firstActiveDate: null,
  // ── v6 tracking ────────────────────────────────────────────────────────
  // Per-badge "first earned at" ISO date stamp. Populated going forward as
  // commitSession / commitMiniGame mints new badges — pre-v6 earned badges
  // remain unstamped (the UI shows just "Κερδισμένο!" without a date).
  // Shape: { [badgeId]: 'YYYY-MM-DD' }
  badgesEarnedAt: {},
  // ── v7 tracking ────────────────────────────────────────────────────────
  // Λεξοδάσος (forest) growth per error_tag. Keyed by canonical tag name
  // from `KNOWN_ERROR_TAGS`. Each entry mirrors the main pet shape:
  //   { growth: number, stage: number }
  // Tags missing from the map mean "child has not encountered this tag yet"
  // — the UI shows the species as a seed. See `utils/forestGrowth.js` for
  // the growth rules.
  forest: {},
  // ── v8 tracking (sync layer — DORMANT) ──────────────────────────────────
  // Pseudonymous identity + sync bookkeeping. profileId/deviceId stay null
  // here: id generation is a side-effect the sync layer performs lazily, so
  // THIS migration stays pure (no randomness). Nothing offline reads these.
  profileId: null,
  deviceId: null,
  syncMeta: { ...DEFAULT_SYNC_META },
  outbox: [],
});

function deepCloneDefault(value) {
  if (value === null || typeof value !== 'object') return value;
  return JSON.parse(JSON.stringify(value));
}

export function freshProgress() {
  const out = {};
  for (const key of Object.keys(FALLBACK)) {
    out[key] = deepCloneDefault(FALLBACK[key]);
  }
  return out;
}

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Migrate raw stored progress data to the current schema version.
 *
 * @param {*} raw - The value returned by storage.getItem (already JSON-parsed
 *                  by the storage abstraction). May be null/undefined/corrupt.
 * @returns {{ data: object, migrated: boolean, fromVersion: number,
 *             toVersion: number, hadValidInput: boolean }}
 */
export function migrateProgressData(raw) {
  if (!isPlainObject(raw)) {
    return {
      data: freshProgress(),
      migrated: raw !== null && raw !== undefined,
      fromVersion: 0,
      toVersion: CURRENT_SCHEMA_VERSION,
      hadValidInput: false,
    };
  }

  // Step 0: capture the incoming schema version BEFORE filling in defaults
  // (the FALLBACK below includes `schemaVersion: CURRENT_SCHEMA_VERSION`,
  // which would otherwise mask "no version" inputs as already-current).
  const incomingVersion =
    typeof raw.schemaVersion === 'number' && raw.schemaVersion >= 1
      ? raw.schemaVersion
      : 1;

  const out = { ...raw };
  let migrated = false;

  // Step 1: fill missing fields with defaults (additive — never overwrites).
  for (const key of Object.keys(FALLBACK)) {
    if (out[key] === undefined) {
      out[key] = deepCloneDefault(FALLBACK[key]);
      migrated = true;
    }
  }

  let v = incomingVersion;

  // Step 3: per-version migrations. Each block translates the slice from
  // version v → v+1. Order matters; never edit completed steps.
  if (v < 2) {
    // v1 → v2: introduce `completedTrainingDays` (calendar of daily-quest
    // completions). Defaults to empty array.
    if (!Array.isArray(out.completedTrainingDays)) {
      out.completedTrainingDays = [];
    }
    v = 2;
  }
  if (v < 3) {
    // v2 → v3: introduce extended badge/hat tracking. Each field defaults
    // to 0 (or the empty stats map) so a user upgrading from v2 starts
    // earning these from scratch without losing prior progress.
    if (!Number.isFinite(out.bestCorrectStreak)) out.bestCorrectStreak = 0;
    if (!Number.isFinite(out.currentCorrectStreak)) out.currentCorrectStreak = 0;
    if (!Number.isFinite(out.reviewsCompleted)) out.reviewsCompleted = 0;
    if (!Number.isFinite(out.dailyQuestsCompleted)) {
      out.dailyQuestsCompleted = 0;
    }
    if (!isPlainObject(out.exerciseTypeStats)) {
      out.exerciseTypeStats = { ...DEFAULT_EXERCISE_TYPE_STATS };
    }
    if (out.lastPlayedGrade !== null && typeof out.lastPlayedGrade !== 'string') {
      out.lastPlayedGrade = null;
    }
    v = 3;
  }
  if (v < 4) {
    // v3 → v4: introduce the rolling exercise history list and the weekly
    // mandatory-review completion log. Both default to empty arrays so a
    // user upgrading from v3 starts contributing entries from their next
    // commit; nothing prior is lost.
    if (!Array.isArray(out.exerciseHistory)) out.exerciseHistory = [];
    if (!Array.isArray(out.weeklyReviewDates)) out.weeklyReviewDates = [];
    v = 4;
  }
  if (v < 5) {
    // v4 → v5: introduce the daily-quest archive (drives calendar make-up)
    // and the firstActiveDate gate. Existing users get firstActiveDate
    // backfilled from lastSessionDate if available, so their calendar
    // make-up window starts where they actually were active (capped to
    // this Mon-Fri week by the eligibility check at read time).
    if (!isPlainObject(out.dailyQuestHistory)) out.dailyQuestHistory = {};
    // Step 1 above already filled firstActiveDate with the FALLBACK (null).
    // For users coming from v4 we want to backfill from lastSessionDate
    // when available — only override here if it's still null.
    if (out.firstActiveDate == null && typeof out.lastSessionDate === 'string') {
      out.firstActiveDate = out.lastSessionDate;
    }
    v = 5;
  }
  if (v < 6) {
    // v5 → v6: introduce per-badge earned-at timestamps. Pre-existing badges
    // stay un-stamped (we don't know when they were earned). Only badges
    // minted from now on get a date — the modal renders "Κερδισμένο!"
    // alone for the legacy ones and "Κερδισμένο στις {date}" once stamped.
    if (!isPlainObject(out.badgesEarnedAt)) out.badgesEarnedAt = {};
    v = 6;
  }
  if (v < 7) {
    // v6 → v7: introduce per-tag forest growth. Brand-new field, no back-
    // fill — existing players start with an empty forest the moment their
    // main λεξοδέντρο hits stage 10. Stages 0-4 of the main pet kept their
    // original thresholds in this migration, so anyone already on stage 4
    // sees the new 5-10 stages as additional progression rather than a
    // regression.
    if (!isPlainObject(out.forest)) out.forest = {};
    v = 7;
  }
  if (v < 8) {
    // v7 → v8: dormant sync fields (profileId/deviceId/syncMeta/outbox) were
    // already filled by the additive default pass above. Nothing else to do —
    // ids are generated lazily by the sync layer, keeping this migration pure.
    v = 8;
  }

  if (v !== incomingVersion) migrated = true;
  out.schemaVersion = CURRENT_SCHEMA_VERSION;

  // Step 4: defensive type coercion. Corrupt or partially-written fields
  // are reset to safe defaults rather than crashing later in the app.
  // Use Number.isFinite, since `typeof NaN === 'number'` would otherwise
  // let NaN slip through.
  if (!Number.isFinite(out.xp)) out.xp = 0;
  if (!Number.isFinite(out.currentStreak) || out.currentStreak < 0)
    out.currentStreak = 0;
  if (!Number.isFinite(out.bestStreak) || out.bestStreak < 0)
    out.bestStreak = 0;
  if (out.lastSessionDate !== null && typeof out.lastSessionDate !== 'string') {
    out.lastSessionDate = null;
  }
  if (!Array.isArray(out.badges)) out.badges = [];
  if (!Number.isFinite(out.totalSessions) || out.totalSessions < 0)
    out.totalSessions = 0;
  if (!Number.isFinite(out.perfectSessions) || out.perfectSessions < 0)
    out.perfectSessions = 0;
  if (!isPlainObject(out.weaknessProfile)) out.weaknessProfile = {};
  if (!isPlainObject(out.itemTracking)) out.itemTracking = {};
  if (!isPlainObject(out.dailyQuests)) {
    out.dailyQuests = { date: null, quests: [] };
  } else if (!Array.isArray(out.dailyQuests.quests)) {
    out.dailyQuests = { date: out.dailyQuests.date ?? null, quests: [] };
  }
  if (!isPlainObject(out.pet)) out.pet = { growth: 0, stage: 0 };
  if (!isPlainObject(out.avatar)) out.avatar = { ...DEFAULT_AVATAR };
  if (!Array.isArray(out.completedTrainingDays)) {
    out.completedTrainingDays = [];
  } else {
    // Dedupe while preserving order; coerce to strings.
    const seen = new Set();
    const cleaned = [];
    for (const d of out.completedTrainingDays) {
      if (typeof d !== 'string') continue;
      if (seen.has(d)) continue;
      seen.add(d);
      cleaned.push(d);
    }
    if (cleaned.length !== out.completedTrainingDays.length) migrated = true;
    out.completedTrainingDays = cleaned;
  }
  if (!Number.isFinite(out.bestCorrectStreak) || out.bestCorrectStreak < 0) {
    out.bestCorrectStreak = 0;
  }
  if (
    !Number.isFinite(out.currentCorrectStreak) ||
    out.currentCorrectStreak < 0
  ) {
    out.currentCorrectStreak = 0;
  }
  if (!Number.isFinite(out.reviewsCompleted) || out.reviewsCompleted < 0) {
    out.reviewsCompleted = 0;
  }
  if (
    !Number.isFinite(out.dailyQuestsCompleted) ||
    out.dailyQuestsCompleted < 0
  ) {
    out.dailyQuestsCompleted = 0;
  }
  if (!isPlainObject(out.exerciseTypeStats)) {
    out.exerciseTypeStats = { ...DEFAULT_EXERCISE_TYPE_STATS };
  } else {
    // Add missing keys, drop invalid values. Preserves any extras a future
    // build might have written.
    const next = { ...DEFAULT_EXERCISE_TYPE_STATS, ...out.exerciseTypeStats };
    for (const k of Object.keys(next)) {
      if (!Number.isFinite(next[k]) || next[k] < 0) next[k] = 0;
    }
    out.exerciseTypeStats = next;
  }
  if (out.lastPlayedGrade !== null && typeof out.lastPlayedGrade !== 'string') {
    out.lastPlayedGrade = null;
  }
  if (!Array.isArray(out.exerciseHistory)) {
    out.exerciseHistory = [];
  } else {
    // Drop malformed entries and cap the length.
    const cleaned = out.exerciseHistory
      .filter(
        (e) =>
          isPlainObject(e) &&
          typeof e.itemId === 'string' &&
          typeof e.attemptedAt === 'string'
      )
      .slice(-EXERCISE_HISTORY_LIMIT);
    if (cleaned.length !== out.exerciseHistory.length) migrated = true;
    out.exerciseHistory = cleaned;
  }
  if (!Array.isArray(out.weeklyReviewDates)) {
    out.weeklyReviewDates = [];
  } else {
    const seen = new Set();
    const cleaned = [];
    for (const d of out.weeklyReviewDates) {
      if (typeof d !== 'string' || seen.has(d)) continue;
      seen.add(d);
      cleaned.push(d);
    }
    if (cleaned.length !== out.weeklyReviewDates.length) migrated = true;
    out.weeklyReviewDates = cleaned;
  }
  if (!isPlainObject(out.dailyQuestHistory)) {
    out.dailyQuestHistory = {};
  } else {
    // Strip entries whose shape doesn't look like an archived quest slice
    // (defensive against future schema drift). Also cap the size — the
    // archive is a rolling window, not a permanent log.
    const cleaned = {};
    const keys = Object.keys(out.dailyQuestHistory).sort();
    const recent = keys.slice(-DAILY_QUEST_HISTORY_DAYS);
    for (const k of recent) {
      const entry = out.dailyQuestHistory[k];
      if (
        isPlainObject(entry) &&
        Array.isArray(entry.quests) &&
        typeof entry.date === 'string'
      ) {
        cleaned[k] = entry;
      }
    }
    if (Object.keys(cleaned).length !== keys.length) migrated = true;
    out.dailyQuestHistory = cleaned;
  }
  if (out.firstActiveDate !== null && typeof out.firstActiveDate !== 'string') {
    out.firstActiveDate = null;
  }
  if (!isPlainObject(out.badgesEarnedAt)) {
    out.badgesEarnedAt = {};
  } else {
    // Strip non-string values (defensive against future shape drift).
    const cleaned = {};
    for (const [k, v2] of Object.entries(out.badgesEarnedAt)) {
      if (typeof v2 === 'string' && v2.length > 0) cleaned[k] = v2;
    }
    out.badgesEarnedAt = cleaned;
  }
  if (!isPlainObject(out.forest)) {
    out.forest = {};
  } else {
    // Drop malformed entries — only keep { growth: number, stage: number }.
    const cleaned = {};
    for (const [tag, entry] of Object.entries(out.forest)) {
      if (
        isPlainObject(entry) &&
        Number.isFinite(entry.growth) &&
        entry.growth >= 0 &&
        Number.isFinite(entry.stage) &&
        entry.stage >= 0
      ) {
        cleaned[tag] = { growth: entry.growth, stage: entry.stage };
      }
    }
    out.forest = cleaned;
  }
  // ── v8 sync fields (dormant) ────────────────────────────────────────────
  if (out.profileId !== null && typeof out.profileId !== 'string') out.profileId = null;
  if (out.deviceId !== null && typeof out.deviceId !== 'string') out.deviceId = null;
  out.syncMeta = isPlainObject(out.syncMeta)
    ? { ...DEFAULT_SYNC_META, ...out.syncMeta }
    : { ...DEFAULT_SYNC_META };
  if (!Array.isArray(out.outbox)) out.outbox = [];

  return {
    data: out,
    migrated,
    fromVersion: incomingVersion,
    toVersion: CURRENT_SCHEMA_VERSION,
    hadValidInput: true,
  };
}
